/**
 * soql-parser/index.js - Minimal SOQL parser
 *
 * Provides regex-based SOQL parsing to support Monaco editor
 * diagnostics and completions without a full ANTLR parser.
 */

/**
 * SOQLLexer - token type constants used by completion provider.
 */
export const SOQLLexer = {
  // Token types
  T__0: 1, SELECT: 2, FROM: 3, WHERE: 4, ORDER: 5, BY: 6,
  LIMIT: 7, OFFSET: 8, AND: 9, OR: 10, NOT: 11, IN: 12,
  LIKE: 13, NULL: 14, TRUE: 15, FALSE: 16, ASC: 17, DESC: 18,
  NULLS: 19, FIRST: 20, LAST: 21, GROUP: 22, HAVING: 23,
  COUNT: 24, AVG: 25, SUM: 26, MIN: 27, MAX: 28,
  IDENTIFIER: 40, DOT: 41, COMMA: 42, LPAREN: 43, RPAREN: 44,
  EQ: 45, NEQ: 46, LT: 47, GT: 48, LTE: 49, GTE: 50,
  STRING_LITERAL: 51, INT: 52, DECIMAL: 53,
};

/**
 * Parse a SOQL query string.
 *
 * @param {string} query
 * @returns {{ errors: Array, tree: object }}
 */
export function parse(query) {
  const errors = [];
  let tree = null;

  try {
    tree = parseSOQL(query);
  } catch (e) {
    errors.push({
      line: 1,
      column: 0,
      message: e.message,
      offendingSymbol: null,
    });
  }

  return { errors, tree };
}

/**
 * Extract field and object references from a parsed tree.
 *
 * @param {object} tree
 * @returns {{ mainObject: string|null, fields: string[], relationships: string[][] }}
 */
export function extractReferences(tree) {
  if (!tree) return { mainObject: null, fields: [], relationships: [] };

  return {
    mainObject: tree.fromClause || null,
    fields: tree.simpleFields || [],
    relationships: tree.relationshipFields || [],
  };
}

/**
 * Get tokens at a specific position in the query text.
 * Used by completion provider for context-aware completions.
 *
 * Returns a structured object with:
 *   - context: SOQL clause context ('SELECT_FIELDS', 'FROM_OBJECT', 'AFTER_FROM',
 *              'WHERE_CONDITION', 'GROUP_BY', 'ORDER_BY', 'HAVING', or null)
 *   - prefix: partial identifier being typed at cursor (empty string if none)
 *   - previousToken: last complete token before cursor, or null
 *   - allTokens: all complete tokens before the cursor
 *
 * @param {string} text - Full query text
 * @param {number} offset - Character offset (from model.getOffsetAt)
 * @returns {{ context: string|null, prefix: string, previousToken: object|null, allTokens: object[] }}
 */
export function getTokensAtPosition(text, offset) {
  const tokens = tokenize(text);

  // If cursor is at a whitespace boundary the preceding token is complete.
  // If cursor is at end-of-string or mid-word, the identifier at cursor is still being typed.
  const charAtCursor = offset < text.length ? text[offset] : '';
  const atWhitespaceBoundary = /\s/.test(charAtCursor);

  // Find the token that spans the cursor (being partially typed)
  const partialToken = atWhitespaceBoundary
    ? null
    : tokens.find(
        t => t.type === SOQLLexer.IDENTIFIER && t.start < offset && t.start + t.text.length >= offset
      );

  // prefix = characters of the partial identifier typed so far
  const prefix = partialToken ? partialToken.text.substring(0, offset - partialToken.start) : '';

  // Complete tokens strictly before the cursor (exclude the partial one)
  const allTokens = tokens.filter(t => t !== partialToken && t.start < offset);

  const previousToken = allTokens.length > 0 ? allTokens[allTokens.length - 1] : null;

  // Walk token sequence with a state machine to determine current SOQL context
  let context = null;
  for (let i = 0; i < allTokens.length; i++) {
    const tok = allTokens[i];
    switch (tok.type) {
      case SOQLLexer.SELECT:
        context = 'SELECT_FIELDS';
        break;
      case SOQLLexer.FROM:
        context = 'FROM_OBJECT';
        break;
      case SOQLLexer.WHERE:
        context = 'WHERE_CONDITION';
        break;
      case SOQLLexer.HAVING:
        context = 'HAVING';
        break;
      case SOQLLexer.GROUP:
        if (allTokens[i + 1]?.type === SOQLLexer.BY) {
          context = 'GROUP_BY';
          i++; // consume BY
        }
        break;
      case SOQLLexer.ORDER:
        if (allTokens[i + 1]?.type === SOQLLexer.BY) {
          context = 'ORDER_BY';
          i++; // consume BY
        }
        break;
      case SOQLLexer.IDENTIFIER:
        // Seeing the object name after FROM transitions to AFTER_FROM
        if (context === 'FROM_OBJECT') {
          context = 'AFTER_FROM';
        }
        break;
      default:
        break;
    }
  }

  return { context, prefix, previousToken, allTokens };
}

// ---------------------------------------------------------------------------
// Internal implementation
// ---------------------------------------------------------------------------

function tokenize(text) {
  const tokens = [];
  const keywords = {
    SELECT: SOQLLexer.SELECT, FROM: SOQLLexer.FROM, WHERE: SOQLLexer.WHERE,
    ORDER: SOQLLexer.ORDER, BY: SOQLLexer.BY, LIMIT: SOQLLexer.LIMIT,
    OFFSET: SOQLLexer.OFFSET, AND: SOQLLexer.AND, OR: SOQLLexer.OR,
    NOT: SOQLLexer.NOT, IN: SOQLLexer.IN, LIKE: SOQLLexer.LIKE,
    NULL: SOQLLexer.NULL, TRUE: SOQLLexer.TRUE, FALSE: SOQLLexer.FALSE,
    ASC: SOQLLexer.ASC, DESC: SOQLLexer.DESC, NULLS: SOQLLexer.NULLS,
    FIRST: SOQLLexer.FIRST, LAST: SOQLLexer.LAST, GROUP: SOQLLexer.GROUP,
    HAVING: SOQLLexer.HAVING, COUNT: SOQLLexer.COUNT, AVG: SOQLLexer.AVG,
    SUM: SOQLLexer.SUM, MIN: SOQLLexer.MIN, MAX: SOQLLexer.MAX,
  };

  const tokenRegex = /\s+|'[^']*'|[0-9]+(?:\.[0-9]+)?|[a-zA-Z_][a-zA-Z0-9_]*|[.,()=<>!]+/g;
  let match;

  while ((match = tokenRegex.exec(text)) !== null) {
    const value = match[0];
    if (/^\s+$/.test(value)) continue;

    const upper = value.toUpperCase();
    const type = keywords[upper] || SOQLLexer.IDENTIFIER;
    tokens.push({ type, text: value, start: match.index });
  }

  return tokens;
}

function parseSOQL(query) {
  if (!query || !query.trim()) {
    return { fromClause: null, simpleFields: [], relationshipFields: [] };
  }

  // Extract fields from SELECT clause
  const selectMatch = query.match(/SELECT\s+([\s\S]+?)\s+FROM\s+(\w+)/i);
  if (!selectMatch) {
    return { fromClause: null, simpleFields: [], relationshipFields: [] };
  }

  const fieldsStr = selectMatch[1];
  const mainObject = selectMatch[2];

  const simpleFields = [];
  const relationshipFields = [];

  // Parse field list
  const fieldList = fieldsStr.split(',').map(f => f.trim()).filter(Boolean);
  for (const field of fieldList) {
    if (field.includes('(')) continue; // skip aggregate functions
    if (field.includes('.')) {
      const parts = field.split('.').map(p => p.trim());
      relationshipFields.push(parts);
    } else {
      simpleFields.push(field.toLowerCase());
    }
  }

  return { fromClause: mainObject, simpleFields, relationshipFields };
}
